import { axios } from "utils";

import { ADMIN_FOOTPRINTING_URL } from "consts";

import {
  CropFootprintReturnBody,
  CropSaleData,
  EmissionsFactorRefrigerant,
  FarmFootprintReturnBody,
  FarmLandUseChange,
  FootprintingCropFertilizer,
  FootprintingCropPrimaryData,
  FootprintingCropSalesPayload,
  FootprintingCropSeeding,
  FootprintingCropTillage,
  FootprintingFarmCropPlantProtectionPayload,
  FootprintingFarmPrimaryData,
  FootprintingFarmRefrigerantPayload,
  FootprintingFarmRefrigerantUsage,
  FootprintingFarmResourceConsumption,
  FootprintingFarmRetailerPayload,
  LandUseChangeRequestPayload,
  PurchaserData,
  RetailerPurchase,
  UploadablePlantProtection,
} from "models";

export const getFarmFootprintByYearAdmin = async (farmId: string, year: number): Promise<FarmFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints`, { data: { farmId, year }, hideErrorMessage: true });

export const getFootprintingFarmFootprintsByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<FarmFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${primaryDataId}`, { hideErrorMessage: true });

export const getFootprintingCropFootprintByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<CropFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}`, { hideErrorMessage: true });

export const getCropFootprintByCropIdAndYearAdmin = async (farmId: string, year: number, cropId: string): Promise<CropFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints`, { data: { farmId, year, cropId }, hideErrorMessage: true });

export const getFootprintingCropPlantProtectionAdmin = async (primaryDataId: string): Promise<UploadablePlantProtection[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/plant-protection`);

export const getAdminCropFootprints = async (farmId: string, year: number, cropId: string): Promise<CropFootprintReturnBody> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints`, { data: { farmId, year, cropId } });

export const getFootprintingFarmResourceConsumptionByPrimaryDataIdAdmin = async (
  footprintingFarmPrimaryDataId: string
): Promise<FootprintingFarmResourceConsumption> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/resource-consumption`, { data: { footprintingFarmPrimaryDataId } });

export const getFootprintingEmissionFactorsRefrigerantsAdmin = async (): Promise<EmissionsFactorRefrigerant[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/emission-factors/refrigerants`);

export const getFootprintingFarmRefrigerantUsageByPrimaryDataIdAdmin = async (
  footprintingFarmPrimaryDataId: string
): Promise<FootprintingFarmRefrigerantUsage[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/refrigerant-usage`, { data: { footprintingFarmPrimaryDataId } });

export const getFootprintingFarmRetailersAdmin = async (farmId: string): Promise<RetailerPurchase[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/retailers?farmId=${farmId}`);

export const getFootprintingFarmPurchasesAdmin = async (primaryDataId: string): Promise<RetailerPurchase[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${primaryDataId}/purchases`);

export const getFarmLandUseChangesAdmin = async (farmId: string, year?: number, range?: number): Promise<FarmLandUseChange[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${farmId}/land-use-change?year=${year ?? ""}&range=${range ?? ""}`);

export const getFootprintingCropSeedingsByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<FootprintingCropSeeding[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/seedings`);

export const getFootprintingCropTillagesByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<FootprintingCropTillage[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/tillages`);

export const getFootprintingCropFertilizersMineralsByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<FootprintingCropFertilizer[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/fertilizers-minerals`, { data: { primaryDataId } });

export const getFootprintingCropFertilizersOrganicsByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<FootprintingCropFertilizer[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/fertilizers-organics`, { data: { primaryDataId } });

export const getFootprintingCropPurchasersByFarmIdAdmin = async (farmId: string): Promise<PurchaserData[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/purchasers?farmId=${farmId}`);

export const getFootprintingCropSalesByPrimaryDataIdAdmin = async (primaryDataId: string): Promise<CropSaleData[]> =>
  axios.get(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/sales`);

export const setFootprintingFarmPrimaryDataAdmin = async (
  data: Omit<FootprintingFarmPrimaryData, "id">,
  primaryDataId: string
): Promise<FootprintingFarmPrimaryData> => axios.put(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${primaryDataId}/primary-data`, data);

export const setFootprintingCropPrimaryDataAdmin = async (
  data: Omit<FootprintingCropPrimaryData, "id">,
  primaryDataId?: string
): Promise<FootprintingCropPrimaryData> => axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/primary-data`, data);

export const setFootprintingFarmResourceConsumptionAdmin = async (
  data: FootprintingFarmResourceConsumption,
  primaryDataId?: string
): Promise<FootprintingFarmResourceConsumption> => axios.put(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${primaryDataId}/resource-consumption`, data);

export const setFootprintingFarmRefrigerantUsageAdmin = async (
  data: FootprintingFarmRefrigerantPayload,
  primaryDataId?: string
): Promise<FootprintingFarmRefrigerantUsage[]> => axios.put(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${primaryDataId}/refrigerant-usage`, data);

export const setFootprintingFarmResourceRetailersAdmin = async (data: FootprintingFarmRetailerPayload, primaryDataId?: string): Promise<RetailerPurchase[]> =>
  axios.put(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${primaryDataId}/purchases`, data);

export const setFarmLandUseChangesAdmin = async (
  farmId: string,
  data: LandUseChangeRequestPayload,
  year: number,
  range: number
): Promise<FarmLandUseChange[]> =>
  axios.put(`${ADMIN_FOOTPRINTING_URL}/farm-footprints/${farmId}/land-use-change?year=${year ?? ""}&range=${range ?? ""}`, data);

export const setFootprintingCropSeedingsByPrimaryDataIdAdmin = async (data: FootprintingCropSeeding[], primaryDataId: string) =>
  axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/seedings`, data);

export const setFootprintingCropTillagesByPrimaryDataIdAdmin = async (data: FootprintingCropTillage[], primaryDataId: string) =>
  axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/tillages`, data);

export const setFootprintingCropFertilizersMineralsAdmin = async (
  data: FootprintingCropFertilizer[],
  primaryDataId: string
): Promise<FootprintingCropFertilizer[]> => axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/fertilizers-minerals`, data);

export const setFootprintingCropPlantProtectionAdmin = async (
  data: FootprintingFarmCropPlantProtectionPayload,
  primaryDataId?: string
): Promise<UploadablePlantProtection[]> =>
  axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/plant-protection`, data.selectedPlantProtection);

export const setFootprintingCropSalesAdmin = async (data: FootprintingCropSalesPayload, primaryDataId: string): Promise<CropSaleData[]> =>
  axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/sales`, data);

export const setFootprintingCropFertilizersOrganicsAdmin = async (
  data: FootprintingCropFertilizer[],
  primaryDataId?: string
): Promise<FootprintingCropFertilizer[]> => axios.put(`${ADMIN_FOOTPRINTING_URL}/crop-footprints/${primaryDataId}/fertilizers-organics`, data);
